import {
  ActionConfirmationModal,
  Button,
  Form,
  FormSubmitButton,
  SelectFormField,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { retrieveFirmLeadSourceMappingTargetsOptions } from "@/lib/data/react-query/firm/firm-lead-source";
import {
  useCreateFirmLeadSourceMapping,
  useDeleteFirmLeadSourceMapping,
  useUpdateFirmLeadSourceMapping,
} from "@/lib/data/react-query/firm/firm-lead-source-mapping";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { LeadSourceMapping } from "@/lib/types";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface AddOrEditLeadSourceMappingModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

type ConditionalProps =
  | {
      editing: true;
      activeMapping: LeadSourceMapping;
      leadSourceId: string;
    }
  | { editing: false; activeMapping?: never; leadSourceId: string }
  | {
      editing: false;
      activeMapping?: never;
      leadSourceId?: never;
      onSaveDraft: (
        mapping: Omit<z.infer<typeof formSchema>, "itemLabel">,
      ) => void;
    };

const formSchema = z.object({
  itemLabel: z.string().min(1, { message: "Item is required" }),
  key: z.string().trim().min(1, { message: "Outside key is required" }),
  targetId: z.string().min(1, { message: "Destination is required" }),
});

const defaultValues: z.infer<typeof formSchema> = {
  itemLabel: "",
  key: "",
  targetId: "",
};

const FORM_ID = "lead-source-mapping-form";

export function AddOrEditLeadSourceMappingModal({
  open,
  setOpen,
  leadSourceId,
  editing,
  activeMapping,
  ...props
}: AddOrEditLeadSourceMappingModalProps & ConditionalProps) {
  const onSaveDraft = "onSaveDraft" in props ? props.onSaveDraft : undefined;
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(
      open && editing
        ? {
            itemLabel: activeMapping.target.displayItemLabel,
            key: activeMapping.key,
            targetId: activeMapping.targetId,
          }
        : defaultValues,
    );
  }, [activeMapping, editing, form, open]);

  const createMapping = useCreateFirmLeadSourceMapping(leadSourceId ?? "");
  const updateMapping = useUpdateFirmLeadSourceMapping(leadSourceId ?? "");
  const deleteMapping = useDeleteFirmLeadSourceMapping();
  const {
    data: mappingTargetsData,
    isError: isMappingTargetsError,
    isLoading: isMappingTargetsLoading,
  } = useQuery(retrieveFirmLeadSourceMappingTargetsOptions());
  const mappingTargets = useMemo(
    () => mappingTargetsData?.data ?? [],
    [mappingTargetsData],
  );
  const itemOptions = useMemo(
    () =>
      Array.from(
        new Set(mappingTargets.map((target) => target.displayItemLabel)),
      )
        .sort((left, right) => left.localeCompare(right))
        .map((label) => ({ label, value: label })),
    [mappingTargets],
  );
  const selectedItemLabel = form.watch("itemLabel");
  const attributeOptions = useMemo(
    () =>
      mappingTargets
        .filter((target) => target.displayItemLabel === selectedItemLabel)
        .map((target) => ({
          label: target.displayAttributeLabel,
          value: target.id,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [mappingTargets, selectedItemLabel],
  );

  const canDelete = useMemo(
    () =>
      hasActionPermission(
        ACTION_PERMISSION_KEYS.leadSourceMapping,
        ACTION_PERMISSION_VALUES.delete,
      ),
    [hasActionPermission],
  );

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const mapping = { key: data.key, targetId: data.targetId };
    if (onSaveDraft) {
      onSaveDraft(mapping);
      setOpen(false);
      return;
    }
    if (!leadSourceId) return;

    if (editing) {
      await updateMapping
        .mutateAsync({
          ...mapping,
          leadSourceId,
          id: activeMapping.id,
          dateModified: activeMapping.dateModified,
        })
        .then(() => {
          setOpen(false);
        });
    } else {
      await createMapping
        .mutateAsync({
          ...mapping,
          leadSourceId,
        })
        .then(() => {
          setOpen(false);
        });
    }
  };

  const isFormLoading =
    createMapping.isPending ||
    updateMapping.isPending ||
    deleteMapping.isPending;

  const deleteButton =
    editing && canDelete ? (
      <div>
        <Button
          type="button"
          variant="destructive"
          onClick={() => {
            setIsDeleting(true);
          }}
        >
          Delete
        </Button>
      </div>
    ) : null;

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={editing ? "Edit mapping" : "Add mapping"}
          isDirty={form.formState.isDirty}
          loading={isFormLoading}
          size="xl"
          overlayClassName="z-[108]"
          className="z-[109]"
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              {deleteButton}
              <div className="flex grow justify-end space-x-2">
                <FormCancelButton
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                <FormSubmitButton submitFormId={FORM_ID} />
              </div>
            </div>
          }
        >
          <form
            id={FORM_ID}
            className="flex min-h-0 grow flex-col overflow-hidden"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="overflow-y-auto">
              <fieldset className={slideoverFieldsetClassName}>
                <TextFormField
                  label="Outside key*"
                  register={form.register("key")}
                />
                <SelectFormField
                  label="Item*"
                  items={itemOptions}
                  itemsKey="value"
                  formatItemLabel={(item) => item?.label}
                  placeholder="Select an item"
                  isItemsError={isMappingTargetsError}
                  isItemsLoading={isMappingTargetsLoading}
                  register={form.register("itemLabel")}
                  onChange={() => {
                    form.setValue("targetId", "", { shouldDirty: true });
                  }}
                />
                <SelectFormField
                  label="Attribute*"
                  items={attributeOptions}
                  itemsKey="value"
                  formatItemLabel={(item) => item?.label}
                  placeholder="Select an attribute"
                  emptyMessage="No destinations found."
                  isItemsError={isMappingTargetsError}
                  isItemsLoading={isMappingTargetsLoading}
                  register={form.register("targetId")}
                />
              </fieldset>
            </div>
          </form>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        title="Delete this mapping?"
        open={isDeleting && editing}
        setOpen={setIsDeleting}
        afterActionFn={() => {
          if (editing && leadSourceId) {
            deleteMapping.mutate(
              {
                leadSourceId,
                id: activeMapping.id,
              },
              {
                onSuccess: () => {
                  setOpen(false);
                  setIsDeleting(false);
                },
              },
            );
          }
        }}
      />
    </>
  );
}
